import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Types ── */
interface Terpene {
  id?: number;
  name: string;
  aroma?: string;
  effects?: string[];
  description?: string;
  found_in_strains?: string[];
  boiling_point?: string;
}

type EffectCategory = 'all' | 'relaxing' | 'energizing' | 'pain' | 'anxiety';

/* ── Helpers ── */
function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

const EFFECT_FILTERS: { key: EffectCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'relaxing', label: 'Relaxing' },
  { key: 'energizing', label: 'Energizing' },
  { key: 'pain', label: 'Pain Relief' },
  { key: 'anxiety', label: 'Anti-Anxiety' },
];

const RELAXING_KEYWORDS = ['relax', 'sedat', 'calm', 'sleep', 'tranquil'];
const ENERGIZING_KEYWORDS = ['energy', 'uplift', 'alert', 'focus', 'stimulat', 'euphori'];
const PAIN_KEYWORDS = ['pain', 'anti-inflam', 'analgesic', 'inflammat'];
const ANXIETY_KEYWORDS = ['anxiety', 'anxiolytic', 'stress', 'anti-anxiety'];

function classifyTerpene(t: Terpene): EffectCategory {
  const effects = Array.isArray(t.effects) ? t.effects : typeof t.effects === 'string' ? [t.effects] : [];
  const text = [
    ...effects.map(e => typeof e === 'string' ? e : String(e)),
    typeof t.description === 'string' ? t.description : '',
    typeof t.aroma === 'string' ? t.aroma : '',
  ]
    .join(' ')
    .toLowerCase();

  if (ANXIETY_KEYWORDS.some(k => text.includes(k))) return 'anxiety';
  if (PAIN_KEYWORDS.some(k => text.includes(k))) return 'pain';
  if (RELAXING_KEYWORDS.some(k => text.includes(k))) return 'relaxing';
  if (ENERGIZING_KEYWORDS.some(k => text.includes(k))) return 'energizing';
  return 'all'; // neutral / uncategorized
}

function categoryColors(cat: EffectCategory): {
  border: string;
  bg: string;
  accent: string;
} {
  switch (cat) {
    case 'relaxing':
      return {
        border: 'border-purple-500/30',
        bg: 'bg-purple-500/[0.06]',
        accent: 'text-purple-300',
      };
    case 'energizing':
      return {
        border: 'border-orange-500/30',
        bg: 'bg-orange-500/[0.06]',
        accent: 'text-orange-300',
      };
    case 'pain':
      return {
        border: 'border-blue-500/30',
        bg: 'bg-blue-500/[0.06]',
        accent: 'text-blue-300',
      };
    case 'anxiety':
      return {
        border: 'border-green-500/30',
        bg: 'bg-green-500/[0.06]',
        accent: 'text-green-300',
      };
    default:
      return {
        border: 'border-white/10',
        bg: 'bg-white/[0.02]',
        accent: 'text-white/60',
      };
  }
}

function categoryBadge(cat: EffectCategory): { label: string; classes: string } | null {
  switch (cat) {
    case 'relaxing':
      return { label: 'Relaxing', classes: 'bg-purple-500/20 text-purple-300 border-purple-500/30' };
    case 'energizing':
      return { label: 'Energizing', classes: 'bg-orange-500/20 text-orange-300 border-orange-500/30' };
    case 'pain':
      return { label: 'Pain Relief', classes: 'bg-blue-500/20 text-blue-300 border-blue-500/30' };
    case 'anxiety':
      return { label: 'Anti-Anxiety', classes: 'bg-green-500/20 text-green-300 border-green-500/30' };
    default:
      return null;
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function TerpeneWheelTab(_props: CountryProps) {
  const [terpenes, setTerpenes] = useState<Terpene[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<EffectCategory>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(getApiUrl('/api/terpenes'));
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setTerpenes(Array.isArray(data) ? data : []);
      } catch {
        setTerpenes([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const classified = useMemo(
    () => terpenes.map(t => ({ ...t, category: classifyTerpene(t) })),
    [terpenes]
  );

  const filtered = useMemo(() => {
    return classified.filter(t => {
      if (filter !== 'all' && t.category !== filter) return false;
      if (typeof search === 'string' && search.trim()) {
        const q = search.toLowerCase();
        const searchable = [
          t.name,
          t.aroma || '',
          ...(t.effects || []),
          ...(t.found_in_strains || []),
        ]
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
  }, [classified, filter, search]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Terpene Guide Intro */}
      <div className="rounded-2xl border border-[#4ade80]/20 bg-[#4ade80]/[0.04] p-5">
        <h2 className="text-white font-semibold text-base mb-2">What Are Terpenes?</h2>
        <p className="text-white/60 text-sm leading-relaxed">
          Terpenes are aromatic compounds found in cannabis and many other plants. They are responsible
          for the distinct smells and flavors of different strains, and they interact with cannabinoids
          to influence the overall effects — a phenomenon known as the entourage effect. Understanding
          terpene profiles can help you choose strains that match your desired experience.
        </p>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search terpenes by name, aroma, effect, or strain..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ade80]/40 transition-colors"
      />

      {/* Filter Bar */}
      <div className="flex gap-2 flex-wrap">
        {EFFECT_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors ${
              filter === f.key
                ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]'
                : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Count */}
      <p className="text-white/40 text-sm">
        {filtered.length} terpene{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <p>No terpenes match your search or filter.</p>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filtered.map((t, i) => {
            const key = t.id ? String(t.id) : `${t.name}-${i}`;
            const isExpanded = expandedId === key;
            const colors = categoryColors(t.category);
            const badge = categoryBadge(t.category);

            return (
              <motion.div key={key} variants={staggerItem} layout>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : key)}
                  className={`cursor-pointer rounded-2xl border p-4 transition-colors ${colors.border} ${
                    isExpanded ? colors.bg : 'bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-white font-semibold text-base">{t.name}</h3>
                    {badge && (
                      <span
                        className={`shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${badge.classes}`}
                      >
                        {badge.label}
                      </span>
                    )}
                  </div>

                  {/* Aroma */}
                  {t.aroma && (
                    <p className="text-white/50 text-xs mb-2">
                      <span className="text-white/30 mr-1">Aroma:</span>
                      {t.aroma}
                    </p>
                  )}

                  {/* Effects */}
                  {t.effects && t.effects.length > 0 && (
                    <div className="mb-2">
                      <p className="text-white/30 text-[10px] uppercase tracking-wider mb-1 font-medium">
                        Effects
                      </p>
                      <p className={`text-xs ${colors.accent}`}>
                        {t.effects.join(', ')}
                      </p>
                    </div>
                  )}

                  {/* Boiling Point */}
                  {t.boiling_point && (
                    <p className="text-white/40 text-xs">
                      <span className="text-white/25 mr-1">Boiling Point:</span>
                      {t.boiling_point}
                    </p>
                  )}

                  {/* Found in strains — compact */}
                  {t.found_in_strains && t.found_in_strains.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {t.found_in_strains.slice(0, isExpanded ? undefined : 4).map((s, si) => (
                        <span
                          key={si}
                          className="px-2 py-0.5 text-[10px] rounded-full bg-white/5 text-white/40 border border-white/10"
                        >
                          {s}
                        </span>
                      ))}
                      {!isExpanded && t.found_in_strains.length > 4 && (
                        <span className="px-2 py-0.5 text-[10px] text-white/25">
                          +{t.found_in_strains.length - 4} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Expanded: Description */}
                  <AnimatePresence>
                    {isExpanded && t.description && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1 font-medium">
                            Description
                          </p>
                          <p className="text-white/60 text-xs leading-relaxed">
                            {t.description}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <p className="text-white/20 text-[10px] mt-2">
                    {isExpanded ? 'Click to collapse' : 'Click for more'}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
